import axios from 'axios';
import { API_BASE } from '../config';

const API_URL = `${API_BASE}/admin`;

const getAuthHeader = () => {
    const userStr = localStorage.getItem('xemail_user');
    if (!userStr) {
        console.warn("🔐 No admin session found in localeStorage!");
        return { 'Content-Type': 'application/json' };
    }
    const user = JSON.parse(userStr);
    const tokenPreview = user?.token ? `${user.token.substring(0, 10)}...${user.token.substring(user.token.length - 10)}` : "MISSING";
    console.log(`🚀 Sending Request as: ${user.email} (Role: ${user.role}) [Token: ${tokenPreview}]`);
    return {
        'Authorization': `Bearer ${user?.token}`,
        'Content-Type': 'application/json'
    };
};

const DOMAIN_API_URL = `${API_URL}/domains`;

const adminService = {
    // ... rest of the existing methods ...
    getStats: async () => {
        const response = await axios.get(`${API_URL}/stats`, { headers: getAuthHeader() });
        return response.data;
    },
    getUsers: async () => {
        const response = await axios.get(`${API_URL}/users`, { headers: getAuthHeader() });
        return response.data;
    },
    createUser: async (userData) => {
        const response = await axios.post(`${API_URL}/users`, userData, { headers: getAuthHeader() });
        return response.data;
    },
    toggleUserStatus: async (userId, enabled) => {
        const response = await axios.put(`${API_URL}/users/${userId}/status?enabled=${enabled}`, {}, { headers: getAuthHeader() });
        return response.data;
    },
    deleteUser: async (userId) => {
        const response = await axios.delete(`${API_URL}/users/${userId}`, { headers: getAuthHeader() });
        return response.data;
    },
    assignRole: async (userId, role) => {
        const response = await axios.patch(`${API_URL}/users/${userId}/role?role=${role}`, {}, { headers: getAuthHeader() });
        return response.data;
    },
    resetPassword: async (userId, newPassword) => {
        const response = await axios.post(`${API_URL}/users/${userId}/reset-password`, { newPassword }, { headers: getAuthHeader() });
        return response.data;
    },
    updateUser: async (userId, userData) => {
        const response = await axios.patch(`${API_URL}/users/${userId}`, userData, { headers: getAuthHeader() });
        return response.data;
    },
    getEmails: async (filters = {}) => {
        const params = new URLSearchParams(filters).toString();
        const response = await axios.get(`${API_URL}/emails?${params}`, { headers: getAuthHeader() });
        return response.data;
    },
    getSettings: async () => {
        const response = await axios.get(`${API_URL}/config`, { headers: getAuthHeader() });
        return response.data;
    },
    updateSettings: async (settings) => {
        const response = await axios.put(`${API_URL}/config`, settings, { headers: getAuthHeader() });
        return response.data;
    },
    getAuditLogs: async () => {
        const response = await axios.get(`${API_URL}/logs/audit`, { headers: getAuthHeader() });
        return response.data;
    },
    getLoginLogs: async () => {
        const response = await axios.get(`${API_URL}/logs/login`, { headers: getAuthHeader() });
        return response.data;
    },
    getDashboardConfig: async () => {
        const response = await axios.get(`${API_URL}/dashboard-config`, { headers: getAuthHeader() });
        return response.data;
    },
    updateDashboardConfig: async (config) => {
        const response = await axios.post(`${API_URL}/dashboard-config`, config, { headers: getAuthHeader() });
        return response.data;
    },
    getSystemHealth: async () => {
        const response = await axios.get(`${API_URL}/system/health`, { headers: getAuthHeader() });
        return response.data;
    },
    // Domain Methods
    getDomains: async (tenantId) => {
        const response = await axios.get(`${DOMAIN_API_URL}/tenant/${tenantId}`, { headers: getAuthHeader() });
        return response.data;
    },
    createDomain: async (domainData) => {
        const response = await axios.post(`${DOMAIN_API_URL}`, domainData, { headers: getAuthHeader() });
        return response.data;
    },
    getDomainRecords: async (domainId) => {
        const response = await axios.get(`${DOMAIN_API_URL}/${domainId}/records`, { headers: getAuthHeader() });
        return response.data;
    },
    verifyDomain: async (domainId) => {
        const response = await axios.post(`${DOMAIN_API_URL}/${domainId}/verify`, {}, { headers: getAuthHeader() });
        return response.data;
    },
    deleteDomain: async (domainId) => {
        const response = await axios.delete(`${DOMAIN_API_URL}/${domainId}`, { headers: getAuthHeader() });
        return response.data;
    },
    getOAuthUrl: async (domainId, provider) => {
        const response = await axios.get(`${DOMAIN_API_URL}/${domainId}/oauth-url?provider=${provider}`, { headers: getAuthHeader() });
        return response.data; // { url, provider, requiresApiCredentials, credentialHint?, domainId? }
    },
    connectWithCredentials: async (domainId, credential) => {
        const response = await axios.post(`${DOMAIN_API_URL}/${domainId}/connect-credentials`, { credential }, { headers: getAuthHeader() });
        return response.data;
    },
    getOAuthStatus: async (domainId) => {
        const response = await axios.get(`${DOMAIN_API_URL}/${domainId}/oauth-status`, { headers: getAuthHeader() });
        return response.data; // { connected, tokenAlive, tokenExpiry, provider }
    },
    checkAccess: async () => {
        const response = await axios.get(`${API_URL}/access-check`, { headers: getAuthHeader() });
        return response.data;
    },
    // Enterprise Policy Methods
    getPolicies: async () => {
        const response = await axios.get(`${API_URL}/policies`, { headers: getAuthHeader() });
        return response.data;
    },
    updatePolicies: async (category, policies) => {
        const response = await axios.put(`${API_URL}/policies/${category}`, policies, { headers: getAuthHeader() });
        return response.data;
    },
    // Shared Mailbox Methods
    getSharedMailboxes: async () => {
        const response = await axios.get(`${API_URL}/shared-mailboxes`, { headers: getAuthHeader() });
        return response.data;
    },
    createSharedMailbox: async (data) => {
        const response = await axios.post(`${API_URL}/shared-mailboxes`, data, { headers: getAuthHeader() });
        return response.data;
    },
    deleteSharedMailbox: async (id) => {
        const response = await axios.delete(`${API_URL}/shared-mailboxes/${id}`, { headers: getAuthHeader() });
        return response.data;
    },
    getSharedPermissions: async (id) => {
        const response = await axios.get(`${API_URL}/shared-mailboxes/${id}/permissions`, { headers: getAuthHeader() });
        return response.data;
    },
    addSharedPermission: async (id, data) => {
        const response = await axios.post(`${API_URL}/shared-mailboxes/${id}/permissions`, data, { headers: getAuthHeader() });
        return response.data;
    },
    removeSharedPermission: async (permId) => {
        const response = await axios.delete(`${API_URL}/shared-mailboxes/permissions/${permId}`, { headers: getAuthHeader() });
        return response.data;
    },
    toggleUserMfa: async (userId, enabled) => {
        const response = await axios.put(`${API_URL}/users/${userId}/mfa?enabled=${enabled}`, {}, { headers: getAuthHeader() });
        return response.data;
    },
    resetUserMfa: async (userId) => {
        const response = await axios.post(`${API_URL}/users/${userId}/mfa/reset`, {}, { headers: getAuthHeader() });
        return response.data;
    }
};

export default adminService;

